import { formatMoney } from '@betterment/core';
import { Box, Separator, Text } from '@betterment/samba-react';
import { ActivityDrawerLayout } from '@betterment/shared-components/src/components/activity/components/drawer/common/ActivityDrawerLayout';
import { DrawerListRow } from '@betterment/shared-components/src/components/activity/components/drawer/common/DrawerListRow';
import { getLatestStatus } from '@betterment/shared-components/src/components/activity/utils/statusUtils';

import { ActivityItemFragment } from '../../../../graphql/fragments/activityItem.generated';
import { ActivityItemStage } from '../../../../graphql/schema';
import { NarrowTypename } from '../../../../graphql/utils/narrow-typename';
import { IdempotencyProviderWithFeatureRegistry } from '../../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { resolveAccountName } from '../../../utils/accountNameUtils';
import { CancelButton } from '../common/CancelButton';
import { StatusSection } from '../common/StatusSection';

export const Withdrawal: React.FC<{
  item: NarrowTypename<ActivityItemFragment, 'ActivityItemWithdrawal'>;
}> = ({ item }) => {
  const latestStatus = getLatestStatus(item.statuses);
  const isCanceled = latestStatus?.stage === ActivityItemStage.Canceled;
  const isCompleted =
    latestStatus?.stage === ActivityItemStage.Sent || latestStatus?.stage === ActivityItemStage.Completed;
  const sourceAccountName = resolveAccountName(item.withdrawalSourceAccount);
  const destinationAccountName = resolveAccountName(item.withdrawalDestinationAccount);

  return (
    <ActivityDrawerLayout title="Withdrawal">
      <DrawerListRow label="From" value={sourceAccountName} />
      <Separator />
      <DrawerListRow label="To" value={destinationAccountName} />
      <Separator />
      <>
        {item.isFullWithdrawal ? (
          <>
            <DrawerListRow label="Requested amount" value="Full amount" />
            {isCompleted && item.amount && (
              <>
                <Separator />
                <DrawerListRow label="Final amount" value={formatMoney(item.amount)} />
              </>
            )}
          </>
        ) : (
          <>
            {item.amount && (
              <DrawerListRow label="Amount" value={formatMoney(item.amount)} shouldLineThrough={isCanceled} />
            )}
          </>
        )}
        <Separator />
      </>{' '}
      <DrawerListRow label="Note" value={item.transactionMemo?.reason ?? '—'} />
      {item.transactionMemo?.comment && (
        <Box css={{ paddingY: 'space.200' }}>
          <Text variant="text50" color="text.weak">
            {item.transactionMemo.comment}
          </Text>
        </Box>
      )}
      <Separator />
      <StatusSection statuses={item.statuses} />
      {item.isCancelable && (
        <>
          <Separator />
          <IdempotencyProviderWithFeatureRegistry featureName="cancelTransactionRequest">
            <CancelButton
              transactionType="withdrawal"
              transactionId={item.withdrawalRequestId!}
              to={destinationAccountName}
              amount={item.amount ? formatMoney(item.amount) : ''}
            />
          </IdempotencyProviderWithFeatureRegistry>
        </>
      )}
    </ActivityDrawerLayout>
  );
};
